import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';

const formatCurrency = (val) => {
    if (val === null || val === undefined || val === '') return '—';
    return '\u20B9' + Number(val).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

// PMT formula — mirrors PersonalLoanOfferCalculationService::calculateEmi
const calcEmi = (principal, annualRate, tenureMonths) => {
    const P = Number(principal) || 0;
    const n = Number(tenureMonths) || 0;
    const r = (Number(annualRate) / 12) / 100;
    if (P <= 0 || n <= 0) return 0;
    if (r <= 0) return P / n;
    const factor = Math.pow(1 + r, n);
    return (P * r * factor) / (factor - 1);
};

/**
 * EMI Recalculation Dialog.
 *
 * Opens when user clicks the EMI value on an offer card that has a ROI band
 * (roi_is_range === true). Only ROI is editable — salary, loan amount and
 * tenure are read-only (they come from the lead). User enters a ROI between
 * roi_min and roi_max (e.g. 14.5 – 16 from CSV "14.5-16") and the EMI / total
 * interest / total cost recalc live. "Apply" pushes the new values back to
 * the offer card via onApply(newRoi, newEmi, newInterest, newCost).
 */
const EmiRecalculateDialog = ({ visible, offer, onHide, onApply }) => {
    const roiMin = Number(offer?.roi_min ?? 0);
    const roiMax = Number(offer?.roi_max ?? 0);
    const loanAmount = Number(offer?.loan_amount ?? 0);
    const tenure = Number(offer?.tenure ?? 0);
    const processingFee = Number(offer?.processing_fee ?? 0);
    const salary = Number(offer?.salary ?? offer?.effective_salary ?? 0);

    const [roi, setRoi] = useState(roiMin || Number(offer?.roi ?? 0));

    // Reset ROI when dialog opens with a new offer
    useEffect(() => {
        if (visible) {
            setRoi(roiMin || Number(offer?.roi ?? 0));
        }
    }, [visible, offer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Validation: ROI must be between roi_min and roi_max (inclusive)
    const isRoiValid = useMemo(() => {
        if (roi === null || roi === undefined || roi === '') return false;
        const v = Number(roi);
        if (Number.isNaN(v)) return false;
        return v >= roiMin && v <= roiMax;
    }, [roi, roiMin, roiMax]);

    // Live EMI preview — updates as user types
    const preview = useMemo(() => {
        if (!isRoiValid) return null;
        const newEmi = calcEmi(loanAmount, roi, tenure);
        const newInterest = newEmi * tenure - loanAmount;
        const newCost = processingFee + newInterest;
        return {
            emi: Math.round(newEmi),
            totalInterest: Math.round(newInterest),
            totalCost: Math.round(newCost),
        };
    }, [isRoiValid, loanAmount, roi, tenure, processingFee]);

    const handleApply = () => {
        if (!isRoiValid || !preview) return;
        onApply({
            roi: Number(roi),
            emi: preview.emi,
            total_interest: preview.totalInterest,
            total_cost: preview.totalCost,
            recalculated_at_roi: Number(roi),
        });
        onHide();
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-text"
            />
            <Button
                label="Apply"
                icon="pi pi-check"
                onClick={handleApply}
                disabled={!isRoiValid}
                autoFocus
            />
        </div>
    );

    if (!offer) return null;

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-calculator text-primary"></i>
                    <span>Recalculate EMI</span>
                </div>
            }
            footer={footer}
            style={{ width: '450px' }}
            breakpoints={{ '960px': '90vw' }}
            modal
            draggable={false}
            resizable={false}
        >
            <div className="flex flex-column gap-3">
                <div className="text-500 text-sm">
                    {offer.bank_name} — {offer.product_name}
                </div>

                {/* Read-only fields */}
                <div className="grid m-0">
                    <div className="col-6 p-2">
                        <label className="block text-500 text-xs mb-1">Salary</label>
                        <div className="font-semibold">{formatCurrency(salary)}</div>
                    </div>
                    <div className="col-6 p-2">
                        <label className="block text-500 text-xs mb-1">Loan Amount</label>
                        <div className="font-semibold">{formatCurrency(loanAmount)}</div>
                    </div>
                    <div className="col-6 p-2">
                        <label className="block text-500 text-xs mb-1">Tenure</label>
                        <div className="font-semibold">{tenure} months</div>
                    </div>
                    <div className="col-6 p-2">
                        <label className="block text-500 text-xs mb-1">ROI Band</label>
                        <div className="font-semibold">
                            {roiMin}% &ndash; {roiMax}%
                        </div>
                    </div>
                </div>

                <Divider className="my-1" />

                {/* Editable ROI */}
                <div className="field">
                    <label htmlFor="emi-recalc-roi" className="block mb-2 font-medium">
                        Interest Rate (%)
                    </label>
                    <InputNumber
                        id="emi-recalc-roi"
                        value={roi}
                        onValueChange={(e) => setRoi(e.value)}
                        mode="decimal"
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        min={roiMin}
                        max={roiMax}
                        step={0.01}
                        showButtons
                        suffix="%"
                        className={`w-full ${!isRoiValid ? 'p-invalid' : ''}`}
                        inputClassName="w-full"
                    />
                    <small className="block mt-1 text-500">
                        Enter between {roiMin}% and {roiMax}%
                    </small>
                    {!isRoiValid && (roi !== null && roi !== undefined && roi !== '') && (
                        <small className="block mt-1 text-red-500">
                            ROI must be between {roiMin}% and {roiMax}%
                        </small>
                    )}
                </div>

                {/* Live preview */}
                {preview ? (
                    <div
                        className="p-3 border-round"
                        style={{ backgroundColor: 'var(--blue-50)' }}
                    >
                        <div className="flex justify-content-between align-items-center mb-2">
                            <span className="text-sm font-medium text-700">EMI (Monthly)</span>
                            <span className="text-primary font-bold text-2xl">
                                {formatCurrency(preview.emi)}
                            </span>
                        </div>
                        <div className="flex justify-content-between text-sm mb-1">
                            <span className="text-600">Total Interest</span>
                            <span className="font-medium">{formatCurrency(preview.totalInterest)}</span>
                        </div>
                        <div className="flex justify-content-between text-sm">
                            <span className="text-600">Total Cost</span>
                            <span className="font-medium">{formatCurrency(preview.totalCost)}</span>
                        </div>
                    </div>
                ) : (
                    <Message
                        severity="warn"
                        text={`Enter a valid ROI between ${roiMin}% and ${roiMax}% to see the EMI.`}
                        className="w-full"
                    />
                )}
            </div>
        </Dialog>
    );
};

export default EmiRecalculateDialog;
